import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Naklady na nemovitost (R5): typovany klient nad `/v1/properties/:id/costs`,
 * `/v1/costs/:id`, `/v1/cases/:id/costs`, `/v1/costs/summary`,
 * `/v1/finance/cost-categories`, `/v1/finance/costs/settings`.
 *
 * Odpovedi finance rout jsou zatim `zod.any()` (viz packages/finance/src/public/plugin.ts),
 * takze DTO typy jsou tu psane rucne (odpovidaji costDtoSchema) - request body
 * typy naopak chodi primo z generovaneho OpenAPI kontraktu.
 */

export type Recurring = 'none' | 'monthly' | 'yearly';

export interface CostDto {
  readonly id: string;
  readonly tenantId: string;
  readonly propertyId: string;
  readonly caseId: string | null;
  readonly categoryId: string;
  readonly amount?: string;
  readonly currency: string;
  readonly vatRate?: string | null;
  readonly incurredAt: string;
  readonly vendorPartyId: string | null;
  readonly note: string | null;
  readonly documentId: string | null;
  readonly recurring: Recurring;
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CostCategoryDto {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly active: boolean;
  readonly isSystem: boolean;
}

export interface CaseCostsResult {
  readonly items: CostDto[];
  readonly totalsByCurrency: { currency: string; total: string }[];
  readonly commission: { amount: string; currency: string } | null;
  readonly marginByCurrency: { currency: string; margin: string }[];
}

export interface CostsSummaryRow {
  readonly key: string;
  readonly total: string;
  readonly currency: string;
}

export type CreateCostBody = ApiPaths['/v1/properties/{id}/costs']['post']['requestBody']['content']['application/json'];
export type UpdateCostBody = ApiPaths['/v1/costs/{id}']['patch']['requestBody']['content']['application/json'];
export type CostsSummaryGroupBy = ApiPaths['/v1/costs/summary']['get']['parameters']['query']['groupBy'];

export async function listPropertyCosts(propertyId: string): Promise<CostDto[]> {
  const result = await unwrap(apiClient.GET('/v1/properties/{id}/costs', { params: { path: { id: propertyId } } }));
  return (result as { items: CostDto[] }).items;
}

export async function createPropertyCost(propertyId: string, body: CreateCostBody): Promise<CostDto> {
  return (await unwrap(
    apiClient.POST('/v1/properties/{id}/costs', { params: { path: { id: propertyId } }, body }),
  )) as CostDto;
}

export async function updatePropertyCost(id: string, body: UpdateCostBody): Promise<CostDto> {
  return (await unwrap(apiClient.PATCH('/v1/costs/{id}', { params: { path: { id } }, body }))) as CostDto;
}

export async function deletePropertyCost(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/costs/{id}', { params: { path: { id } } }));
}

export async function listCaseCosts(caseId: string): Promise<CaseCostsResult> {
  return (await unwrap(apiClient.GET('/v1/cases/{id}/costs', { params: { path: { id: caseId } } }))) as CaseCostsResult;
}

export interface CaseCostsSummaryItem {
  readonly caseId: string;
  readonly totalsByCurrency: { currency: string; total: string }[];
  readonly commission: { amount: string; currency: string } | null;
  readonly marginByCurrency: { currency: string; margin: string }[];
}

/**
 * Hromadny souhrn nakladu pro vic zakazek jednim requestem (krok O6a,
 * polozka 3) - misto N volani `listCaseCosts` ze seznamu `/zakazky`.
 */
export async function listCaseCostsSummary(caseIds: readonly string[]): Promise<CaseCostsSummaryItem[]> {
  if (caseIds.length === 0) return [];
  const result = await unwrap(
    apiClient.GET('/v1/costs/summary', {
      params: { query: { groupBy: 'case', caseIds: caseIds.join(',') } },
    }),
  );
  return (result as { items: CaseCostsSummaryItem[] }).items;
}

export interface CostsSummaryParams {
  readonly groupBy: CostsSummaryGroupBy;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export async function getCostsSummary(params: CostsSummaryParams): Promise<CostsSummaryRow[]> {
  const result = await unwrap(
    apiClient.GET('/v1/costs/summary', {
      params: {
        query: {
          groupBy: params.groupBy,
          ...(params.from !== undefined ? { from: params.from } : {}),
          ...(params.to !== undefined ? { to: params.to } : {}),
        },
      },
    }),
  );
  return (result as { items: CostsSummaryRow[] }).items;
}

export function costsSummaryCsvUrl(params: CostsSummaryParams): string {
  const search = new URLSearchParams({ groupBy: params.groupBy, format: 'csv' });
  if (params.from !== undefined) search.set('from', params.from);
  if (params.to !== undefined) search.set('to', params.to);
  const base = (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_API_URL ?? 'http://localhost:3000';
  return `${base}/v1/costs/summary?${search.toString()}`;
}

export async function listCostCategories(): Promise<CostCategoryDto[]> {
  const result = await unwrap(apiClient.GET('/v1/finance/cost-categories', {}));
  return (result as { items: CostCategoryDto[] }).items;
}

export async function createCostCategory(code: string, name: string): Promise<CostCategoryDto> {
  return (await unwrap(apiClient.POST('/v1/finance/cost-categories', { body: { code, name } }))) as CostCategoryDto;
}

export async function updateCostCategory(id: string, body: { name?: string; active?: boolean }): Promise<CostCategoryDto> {
  return (await unwrap(apiClient.PATCH('/v1/finance/cost-categories/{id}', { params: { path: { id } }, body }))) as CostCategoryDto;
}

export interface CostsSettingsDto {
  readonly alertPct: number;
  readonly categories: CostCategoryDto[];
}

export async function getCostsSettings(): Promise<CostsSettingsDto> {
  return (await unwrap(apiClient.GET('/v1/finance/costs/settings', {}))) as CostsSettingsDto;
}

export async function updateCostsSettings(alertPct: number): Promise<CostsSettingsDto> {
  return (await unwrap(apiClient.PUT('/v1/finance/costs/settings', { body: { alertPct } }))) as CostsSettingsDto;
}
